import React, { useEffect, useState } from 'react';
import ApiService from "../../services/ApiService";
import Loader from "../../components/Loader";
import { formatCurrency, formatDate } from '../../utils/helper';
import { toast } from 'react-toastify';

const SalesReport = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [totalSales, setTotalSales] = useState(0);
    const [filters, setFilters] = useState({
        start_date: '',
        end_date: ''
    });

    const fetchReport = async () => {
        setLoading(true);
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/reports/sales',
                data: filters
            });
            if (response.data.status) {
                setData(response.data.data.appointments);
                setTotalSales(response.data.data.total_sales);
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to fetch sales report");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchReport();
    };

    const handleExport = () => {
        const csvRows = [];
        const headers = ['Ref No', 'Customer', 'Service', 'Date', 'Status', 'Amount'];
        csvRows.push(headers.join(','));

        data.forEach(item => {
            const values = [
                `"${item.ref_no || ''}"`,
                `"${item.customer ? item.customer.name : ''}"`,
                `"${item.service ? item.service.name : ''}"`,
                `"${formatDate(item.date)}"`,
                `"${item.status}"`,
                `"${item.price}"`
            ];
            csvRows.push(values.join(','));
        });

        const csvContent = 'data:text/csv;charset=utf-8,' + csvRows.join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `sales_report_${new Date().getTime()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Sales Report
                        </h1>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="card mb-5 mb-xl-10">
                        <div className="card-header border-0 pt-6">
                            <div className="card-title">
                                <form onSubmit={handleSearch} className="d-flex align-items-center position-relative my-1 gap-3">
                                    <div className="d-flex align-items-center position-relative">
                                        <input
                                            type="date"
                                            name="start_date"
                                            className="form-control form-control-solid w-150px"
                                            value={filters.start_date}
                                            onChange={handleFilterChange}
                                            placeholder="Start Date"
                                        />
                                    </div>
                                    <div className="d-flex align-items-center position-relative">
                                        <input
                                            type="date"
                                            name="end_date"
                                            className="form-control form-control-solid w-150px"
                                            value={filters.end_date}
                                            onChange={handleFilterChange}
                                            placeholder="End Date"
                                        />
                                    </div>
                                    <button type="submit" className="btn btn-base">Filter</button>
                                </form>
                            </div>
                            <div className="card-toolbar">
                                <div className="d-flex justify-content-end gap-3 align-items-center">
                                    <button 
                                        type="button" 
                                        className="btn btn-light-primary btn-sm"
                                        onClick={handleExport}
                                        disabled={data.length === 0}
                                    >
                                        <i className="fas fa-download me-2"></i> Export CSV
                                    </button>
                                    <div className="fw-bold fs-5 text-gray-800 border border-gray-200 p-2 rounded">
                                        Total Sales: {formatCurrency(totalSales)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card-body py-4">
                            {loading ? <Loader /> : (
                                <div className="table-responsive">
                                    <table className="table align-middle table-row-dashed fs-6 gy-5">
                                        <thead>
                                            <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                                <th>Ref No</th>
                                                <th>Customer</th>
                                                <th>Service</th>
                                                <th>Date</th>
                                                <th>Status</th>
                                                <th className="text-end">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-gray-600 fw-semibold">
                                            {data.length > 0 ? (
                                                data.map((item) => (
                                                    <tr key={item.id}>
                                                        <td>{item.ref_no || '-'}</td>
                                                        <td>{item.customer ? item.customer.name : '-'}</td>
                                                        <td>{item.service ? item.service.name : '-'}</td>
                                                        <td>{formatDate(item.date)}</td>
                                                        <td>
                                                            <span className={`badge badge-light-${item.status === 'completed' ? 'success' : item.status === 'cancelled' ? 'danger' : 'warning'}`}>
                                                                {item.status}
                                                            </span>
                                                        </td>
                                                        <td className="text-end">{formatCurrency(item.price)}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className="text-center">No records found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SalesReport;
